import { z } from 'zod';
import { ColorsSchema, LOOK, NameSchema, SourceRefSchema, about, numberFormat, sourceOf } from '../schema_fields.ts';
import { IN_GAUGE_COLOURS, gaugeColourFields, gaugeLookField } from './gauge_look.ts';

// The thermostat tile: the room's temperature on a dial, and the set point a tap changes. Set up one
// of two ways (device-roles.md TH-1): a thermostat, one device read whole as the thermostat role; or
// separate values, for sources that know no thermostat, the legacy tile's way, which older files are.

const GaugeSchema = z.object({
    min: z.number().default(10).meta(about('From')),
    max: z.number().default(30).meta(about('To')),
    colors: ColorsSchema.default('blue-yellow-red').meta({ ...about('Gradient', 'a named one such as blue-yellow-red, or a list of colours', 'colors'), ...IN_GAUGE_COLOURS, ...LOOK }),
    ...gaugeColourFields,
    look: gaugeLookField,
})
.prefault({})
.meta(about('Gauge', 'the dial'));

/**
 * The first way: a device that is a thermostat (device-roles.md DR-5), nothing else to pick. Strict,
 * so that an older file's options, which name a temperature and a set point, are the other way.
 */
export const ThermostatByDeviceSchema = z.strictObject({
    source: sourceOf(SourceRefSchema, { gives: ['thermostat'], label: 'Thermostat' }),
    numberFormat: numberFormat('0.0'),
    gauge: GaugeSchema,
})
.meta(about('A thermostat', 'a device that is one: its temperature, set point, mode and state together'));

/** The other way: the temperature and the set point from sources of their own, and perhaps a mode. */
export const ThermostatByValuesSchema = z.object({
    temperature: z.object({
        source: sourceOf(NameSchema, { gives: ['number'], label: 'Temperature' }),
        numberFormat: numberFormat('0.0'),
    })
    .meta(about('Temperature', 'the room\'s temperature')),

    setpoint: z.object({
        source: sourceOf(NameSchema, { gives: ['number'], label: 'Set point' }),
        numberFormat: numberFormat('0.0'),
        unit: z.string().default('').meta(about('Unit', 'shown after it')),
        command: z.string().min(1).optional().meta(about('Command', 'what sets it, of those its source takes; set_setpoint when left out', 'command-name')),
    })
    .meta(about('Set point', 'the temperature asked for, which a tap changes')),

    mode: z.object({
        source: sourceOf(NameSchema, { gives: ['text'], label: 'Mode' }),
        command: z.string().min(1).optional().meta(about('Command', 'what changes it, of those its source takes; none to only show it', 'command-name')),
    })
    .optional()
    .meta(about('Mode', 'heat, cool, …: shown with the set point, and changed in the card where it has a command')),

    gauge: GaugeSchema,
})
.meta(about('Separate values', 'a temperature and a set point from any source, such as a sensor and a relay'));

/** One of the two ways (F-3); a new tile, with no options, is a thermostat. */
export const ThermostatOptionsSchema = z.union([ThermostatByDeviceSchema, ThermostatByValuesSchema]).prefault({});

export type ThermostatByValues = z.output<typeof ThermostatByValuesSchema>;
